import React, { useState, useEffect } from 'react';
import { GxZone, GxStandardAction, getStandardActions, triggerAction } from '../../api/guestExperience';

interface QuickActionPanelProps {
  eventId: string;
  zones: GxZone[];
  selectedZoneId?: string;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  actionButton: {
    minWidth: '100px',
    minHeight: '56px',
    flex: '1 1 calc(50% - 0.25rem)',
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    padding: '0.75rem 0.5rem',
  },
  actionIcon: {
    fontSize: '1.5rem',
  },
  actionLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#333',
    textAlign: 'center' as const,
    lineHeight: 1.2,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '1rem',
    color: '#999',
    fontSize: '0.875rem',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '1rem',
    color: '#999',
    fontSize: '0.875rem',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '360px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontSize: '1.125rem',
    fontWeight: 700,
    margin: '0 0 1rem 0',
    textAlign: 'center' as const,
    color: '#1565c0',
  },
  zoneList: {
    maxHeight: '300px',
    overflowY: 'auto' as const,
  },
  zoneButton: {
    width: '100%',
    minHeight: '44px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    marginBottom: '0.5rem',
  },
  zoneBadge: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  zoneLabel: {
    fontWeight: 600,
    color: '#333',
  },
  cancelButton: {
    width: '100%',
    minHeight: '44px',
    backgroundColor: '#f5f5f5',
    border: 'none',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#666',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  toast: {
    position: 'fixed',
    bottom: '2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#2e7d32',
    color: '#fff',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    zIndex: 10000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
  errorToast: {
    position: 'fixed',
    bottom: '2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#d32f2f',
    color: '#fff',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    zIndex: 10000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
  submitting: {
    opacity: 0.7,
    pointerEvents: 'none' as const,
  },
};

export function QuickActionPanel({ eventId, zones, selectedZoneId }: QuickActionPanelProps) {
  const [actions, setActions] = useState<GxStandardAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState<GxStandardAction | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const activeZones = zones.filter((z) => z.active);

  useEffect(() => {
    let cancelled = false;
    async function fetchActions() {
      try {
        const result = await getStandardActions(eventId);
        if (!cancelled) {
          setActions(result.filter((a) => a.active));
        }
      } catch {
        // Silently handle fetch error
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchActions();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleActionTap = (action: GxStandardAction) => {
    if (selectedZoneId) {
      submitAction(action, selectedZoneId);
    } else {
      setPendingAction(action);
    }
  };

  const handleZoneSelect = (zoneId: string) => {
    if (pendingAction) {
      submitAction(pendingAction, zoneId);
    }
  };

  const submitAction = async (action: GxStandardAction, zoneId: string) => {
    setSubmitting(true);
    try {
      await triggerAction(eventId, {
        actionId: action.id,
        zoneId,
      });

      try {
        navigator.vibrate(100);
      } catch {
        // vibrate not supported in all browsers
      }

      setPendingAction(null);
      showToast(`✓ ${action.label}`, 'success');
    } catch (err: unknown) {
      const error = err as { status?: number; message?: string };
      if (error.status === 429) {
        showToast('Too many actions. Please wait a moment.', 'error');
      } else {
        showToast('Failed to trigger action. Try again.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setPendingAction(null);
  };

  if (loading) {
    return <p style={styles.loading}>Loading actions...</p>;
  }

  if (actions.length === 0) {
    return <p style={styles.empty}>No quick actions configured for this event.</p>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        {actions.map((action) => (
          <button
            key={action.id}
            style={styles.actionButton}
            onClick={() => handleActionTap(action)}
            disabled={submitting}
            aria-label={action.label}
          >
            <span style={styles.actionIcon}>{action.icon}</span>
            <span style={styles.actionLabel}>{action.label}</span>
          </button>
        ))}
      </div>

      {/* Zone Picker Modal */}
      {pendingAction && (
        <div style={styles.overlay} onClick={handleClose} role="dialog" aria-modal="true">
          <div
            style={{ ...styles.modal, ...(submitting ? styles.submitting : {}) }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={styles.modalTitle}>Select Zone for "{pendingAction.label}"</h2>
            <div style={styles.zoneList}>
              {activeZones.map((zone) => (
                <button
                  key={zone.id}
                  style={styles.zoneButton}
                  onClick={() => handleZoneSelect(zone.id)}
                  disabled={submitting}
                >
                  <span style={{ ...styles.zoneBadge, backgroundColor: zone.color }} />
                  <span style={styles.zoneLabel}>{zone.name}</span>
                </button>
              ))}
              {activeZones.length === 0 && (
                <p style={{ textAlign: 'center', color: '#999', fontSize: '0.875rem' }}>
                  No active zones available
                </p>
              )}
            </div>
            <button style={styles.cancelButton} onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={toast.type === 'success' ? styles.toast : styles.errorToast}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
